import React, { FC, useCallback, useMemo, useRef, useState } from 'react';

import {
  InputAdornment,
  SxProps,
  Theme,
  TextFieldProps,
  Box,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { isEqual } from 'lodash';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';

import { isEmptyExpressionValue } from '@shared/helpers/expression-factory.helpers';
import {
  getQuantityValueOverrideExpression,
  isNonLiteralQuantityExpression,
} from '@shared/helpers/element-quantity-value-override.helpers';
import { solve } from '@shared/helpers/expression-solving.helpers';
import { ExpressionVariables } from '@shared/helpers/expression-variables.helpers';

import { guards } from '@shared/helpers/typeguards.helpers';
import { isSelectableQuantityUnit } from '@shared/helpers/unit.helpers';
import { ExpressionValue } from '@shared/models/project.model';
import {
  QuantityUnit,
  SelectableQuantityUnit,
} from '@shared/models/unit.model';

import { IconButton } from '@ui/buttons';

import { expressionPanel } from '../ExpressionInputPanel';
import {
  type ExpressionInputChangeEvent,
  type ExpressionInputState,
  toExpressionValue,
  classifyExpressionInputChange,
} from './expression-input-change.helpers';

import { Tooltip, type TooltipProps } from '@ui/Tooltip';
import { makeStyles } from 'tss-react/mui';

import { useHoverWithRefCallback } from '@client/helpers/dom.helpers';
import {
  useObjectMemo,
  useObjectMemoDeep,
} from '@client/hooks/use-object.hooks';

import { useLocalState } from './expression-input.hooks';
import {
  _getTooltipData,
  visibleExpressionError,
} from './get-tooltip-data.util';
import { useReferenceCallback } from './use-stable-callback.hook';
import { UnitNumberInput } from '@ui/UnitNumberInput';
import { useExpressionDisplayValues } from './use-expression-display-values';
import { useFocusInside } from '@client/hooks/use-events.hooks';
import { UnitNumberInputProps } from '@ui/UnitNumberInput/UnitNumberInput';

const useStyles = makeStyles()((theme) => ({
  errorTooltipTitle: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },
}));

/** Default tooltip chrome + leading error icon. */
const ErrorTooltipTitle: React.FC<{ title: TooltipProps['title'] }> = ({
  title,
}) => {
  const { classes } = useStyles();
  return (
    <div className={classes.errorTooltipTitle}>
      <ErrorIcon fontSize="small" color="error" />
      <div>{title}</div>
    </div>
  );
};

const withErrorTooltipTitle = (
  data: Omit<TooltipProps, 'children'>,
): Omit<TooltipProps, 'children'> => ({
  ...data,
  title: <ErrorTooltipTitle title={data.title} />,
});

export type ExpressionInputProps = {
  propertyId: string;
  value?: ExpressionValue;
  fallbackValue?: ExpressionValue;
  variables?: ExpressionVariables;
  onChange: (event: ExpressionInputChangeEvent) => void;
  errorMessage?: string;
  label?: TextFieldProps['label'];
  unit?: QuantityUnit;
  unitEditable?: boolean;
  displayUnit?: string;
  min?: number;
  max?: number;
  helperText?: UnitNumberInputProps['helperText'];
  disabled?: UnitNumberInputProps['disabled'];
  tooltip?: TooltipProps['title'];
  startIcon?: React.ReactNode;
  /**
   * This may or may not work. Input width isn't consistent depending where we render this right now.
   */
  textAlignment?: UnitNumberInputProps['textAlignment'];
  /** tryVertical will set unit width to a fix width to allow vertical alignment of ExpressionInput units with different units */
  unitAlignment?: UnitNumberInputProps['unitAlignment'];
  /** disables all pointer events and makes the input readonly, without affecting styling */
  deactivated?: boolean;
  id?: UnitNumberInputProps['id'];
  name?: UnitNumberInputProps['name'];
  className?: UnitNumberInputProps['className'];
  variant?: UnitNumberInputProps['variant'];
  size?: TextFieldProps['size'];
  onUnitClick?: UnitNumberInputProps['onUnitClick'];
  active?: boolean;
  /** Accessible name of the value input, which carries no visible label */
  'aria-label'?: string;
};

export const ExpressionInput: FC<ExpressionInputProps> = ({
  propertyId,
  value,
  fallbackValue,
  variables,
  onChange: _onChange,
  errorMessage,
  label,
  unit,
  displayUnit,
  unitEditable = false,
  min,
  max,
  helperText,
  disabled = false,
  tooltip,
  startIcon,
  textAlignment = 'right',
  unitAlignment = 'tryVertical',
  deactivated = false,
  id,
  name,
  className,
  variant = 'outlined',
  size = 'small',
  onUnitClick,
  active,
  'aria-label': ariaLabel,
}) => {
  const textFieldRef = useRef<HTMLInputElement>(null);
  const htmlInputRef = useRef<HTMLInputElement>(null);
  const isExpressionPanelMine = expressionPanel.useIsMyReferenceId(propertyId);
  const lastEmittedRef = useRef<ExpressionInputChangeEvent | undefined>(
    undefined,
  );

  const { enqueueSnackbar } = useSnackbar();
  const solveOptions = useObjectMemo({ min, max });

  /**
   * Formula used when classifying numeric commits. When `value` (e.g. quantity `count`)
   * is undefined but `fallbackValue` exists, the UI still shows the fallback formula —
   * passing it here avoids mis-classifying the edit as `expression-clear`.
   */
  const classificationExpression = useMemo(
    () => (value?.expression ?? fallbackValue?.expression) || undefined,
    [value?.expression, fallbackValue?.expression],
  );

  /** Formula used when committing a numeric override (must not copy inherit lines onto `count`). */
  const numericCommitExpression = useMemo(() => {
    if (value !== undefined) {
      return value.expression ?? classificationExpression;
    }
    if (
      fallbackValue &&
      isNonLiteralQuantityExpression(fallbackValue.expression)
    ) {
      return getQuantityValueOverrideExpression({
        count: value,
        fallbackCount: fallbackValue,
      });
    }
    return classificationExpression;
  }, [value, fallbackValue, classificationExpression]);

  /** only used to get error from expressionValue.value to see if the error is from the expression or from the value */
  const validateNumberExpressionValue = useCallback(
    (
      expressionValue?: ExpressionValue,
    ): Error | ExpressionValue | undefined => {
      if (guards.is.undefined(expressionValue)) return undefined; // no value
      if (isEmptyExpressionValue(expressionValue)) return undefined; // empty expression should be undefined
      try {
        // We only want to solve for value without expression, to capture errors only from value
        solve(expressionValue.value, '', variables, solveOptions);
        return expressionValue;
      } catch (e) {
        return e instanceof Error ? e : new Error(String(e));
      }
    },
    [variables, solveOptions],
  );
  const [localValue, setLocalValue] = useLocalState<
    ExpressionValue | undefined | Error
  >(() => validateNumberExpressionValue(value));

  const [localUnit, setLocalUnit] = useLocalState(unit);
  const [numberParseError, setInnerParseError] = useState<Error | undefined>(
    undefined,
  );

  const hasValueOverride =
    value?.value !== undefined ||
    (!(localValue instanceof Error) && localValue?.value !== undefined);
  const expressionError = visibleExpressionError({
    errorMessage,
    numberParseError,
    hasValueOverride,
    hasLocalError: localValue instanceof Error,
  });

  const resetLocalState = useCallback(() => {
    if (isEqual(localValue, value) && isEqual(localUnit, unit)) {
      htmlInputRef.current?.blur();
    } else {
      setLocalValue(validateNumberExpressionValue(value));
      setLocalUnit(unit);
    }
  }, [
    value,
    unit,
    localValue,
    localUnit,
    setLocalValue,
    setLocalUnit,
    validateNumberExpressionValue,
  ]);
  /** local onChange, will not execute onChange if the value is not valid */
  const onChange = useCallback(
    (output: {
      value?: number;
      expression?: string;
      unit?: SelectableQuantityUnit;
    }) => {
      const {
        value: newValue,
        expression: newExpression,
        unit: newUnit,
      } = output;

      // DEV-3220: clearing value on a trivially-zero expression ('0') while a
      // fallback exists should revert to the fallback entirely.
      const isTriviallyClearable =
        fallbackValue !== undefined &&
        newValue === undefined &&
        newExpression === '0';

      const current: ExpressionInputState = {
        value: value?.value,
        expression: classificationExpression,
        unit: isSelectableQuantityUnit(unit) ? unit : undefined,
      };
      const next: ExpressionInputState = isTriviallyClearable
        ? { value: undefined, expression: undefined, unit: newUnit }
        : {
            value: newValue,
            // normalize empty string to undefined so classifier emits expression-clear
            expression: newExpression || undefined,
            unit: newUnit,
          };

      const event = classifyExpressionInputChange(current, next);

      // dedupe consecutive identical emissions (e.g., programmatic blur + blur-commit)
      if (isEqual(event, lastEmittedRef.current)) return;

      lastEmittedRef.current = event;
      _onChange(event);
      // blur after triggered onChange
      htmlInputRef.current?.blur();
    },
    [_onChange, value?.value, classificationExpression, unit, fallbackValue],
  );

  const [isFocused] = useFocusInside(textFieldRef);
  const [isHovered, hoverRef] = useHoverWithRefCallback<HTMLElement>();
  const hasForcedActive = active === true;
  const isActive = hasForcedActive ? true : isFocused || isExpressionPanelMine;

  const [isFxHovered, fxButtonWrapperRef] =
    useHoverWithRefCallback<HTMLDivElement>();

  const handleNumberChange = useCallback(
    (committedValue?: number) => {
      onChange({
        value: committedValue,
        expression: numericCommitExpression,
        unit: isSelectableQuantityUnit(unit) ? unit : undefined,
      });
    },
    [onChange, unit, numericCommitExpression],
  );

  // used for previewing changes from expression panel. local state is reset on panel close
  const onExpressionPanelChange = useCallback(
    (event: ExpressionInputChangeEvent) => {
      if ('count' in event)
        setLocalValue(toExpressionValue(event.count, value));
      else if (event.type === 'expression-clear') setLocalValue(undefined);
      if ('unit' in event) setLocalUnit(event.unit);
    },
    [value, setLocalValue, setLocalUnit],
  );

  const expressionPanelOptions = useObjectMemoDeep({
    referenceId: propertyId,
    expressionValue: localValue instanceof Error ? value : localValue,
    fallbackExpressionValue: fallbackValue,
    unit: localUnit,
    disableUnits: !unitEditable,
    errorMessage,
    variables,
    min,
    max,
    onChange: onExpressionPanelChange,
  });

  // need stable references for onChange and resetLocalState because handleExpressionPanelOpen is async and needs the latest callbacks
  const stableOnChange = useReferenceCallback(onChange);
  const stableResetLocalState = useReferenceCallback(resetLocalState);

  const handleExpressionPanelOpen = useCallback(async () => {
    if (isExpressionPanelMine) return; // don't open yourself again. this may lead to memory leaks (old promise will be abandoned)
    try {
      const { result, type } = await expressionPanel.open(
        expressionPanelOptions,
      );
      switch (type) {
        case 'save':
          stableOnChange({
            value: result.expressionValue?.value,
            expression: result.expressionValue?.expression,
            unit: result.unit,
          });
          break;
        case 'cancel':
          stableResetLocalState();
          break;
      }
      htmlInputRef.current?.focus();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : String(e), {
        variant: 'error',
        autoHideDuration: 5000,
      });
    }
  }, [
    isExpressionPanelMine,
    enqueueSnackbar,
    expressionPanelOptions,
    stableOnChange,
    stableResetLocalState,
  ]);

  const iconButtonSx = useMemo(() => {
    const sx: SxProps<Theme> = { alignSelf: 'center' };
    if (disabled || !isActive) {
      sx.pointerEvents = 'none';
      sx.opacity = 0;
    }
    return sx;
  }, [isActive, disabled]);

  const tooltipDataProps = useObjectMemoDeep({
    localError:
      numberParseError ??
      (guards.is.error(localValue) ? localValue : undefined),
    expressionError,
    isActive,
    isHovered,
    tooltip,
  });

  const tooltipData = useMemo(
    () => _getTooltipData(tooltipDataProps),
    [tooltipDataProps],
  );

  const rootTooltipProps = useMemo<Omit<TooltipProps, 'children'>>(() => {
    switch (tooltipData.location) {
      case 'wrapper':
        return withErrorTooltipTitle(tooltipData.data);
      default:
        return { title: '', open: false };
    }
  }, [tooltipData]);
  const expressionTooltipProps = useMemo<Omit<TooltipProps, 'children'>>(() => {
    switch (tooltipData.location) {
      case 'expressionIcon':
        return withErrorTooltipTitle(tooltipData.data);
      default:
        return { title: 'Edit formula', open: isFxHovered };
    }
  }, [tooltipData, isFxHovered]);

  const startAdornment = useMemo(
    () => (
      <>
        {startIcon && (
          <InputAdornment position="start" sx={{ mr: 1 }}>
            {startIcon}
          </InputAdornment>
        )}
        <InputAdornment
          position="start"
          sx={{ alignItems: 'center', display: 'flex', mr: 1 }}
        >
          <Tooltip {...expressionTooltipProps} arrow>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {!isActive && expressionError && (
                <ErrorIcon fontSize="small" color="error" />
              )}
              {isActive && (
                <div ref={fxButtonWrapperRef}>
                  <IconButton
                    category="ui"
                    icon="fx"
                    size={12}
                    sx={iconButtonSx}
                    aria-label="Edit formula"
                    onClick={handleExpressionPanelOpen}
                  />
                </div>
              )}
            </div>
          </Tooltip>
        </InputAdornment>
      </>
    ),
    [
      isActive,
      expressionError,
      startIcon,
      iconButtonSx,
      fxButtonWrapperRef,
      handleExpressionPanelOpen,
      expressionTooltipProps,
    ],
  );

  // compute display placeholder and unit using explicit modes (preview/editing/inactive)
  const displayValues = useExpressionDisplayValues({
    originalValue: value,
    isExpressionPanelMine,
    isFocused,
    localValue,
    fallbackValue,
    localUnit,
    displayUnit,
    variables,
    solveOptions,
  });

  return (
    <Tooltip {...rootTooltipProps} arrow>
      <Box ref={hoverRef}>
        <UnitNumberInput
          id={id ?? `${propertyId}-input`}
          name={name ?? propertyId}
          aria-label={ariaLabel}
          value={displayValues.value}
          placeholder={displayValues.placeholder}
          placeholderNumber={displayValues.placeholderNumber}
          fillOnTabValue={displayValues.placeholderNumber}
          ref={textFieldRef}
          inputRef={htmlInputRef}
          className={className}
          error={numberParseError || guards.is.error(localValue)}
          unit={displayValues.unit}
          displayUnit={displayUnit}
          unitAlignment={unitAlignment}
          min={min}
          max={max}
          deactivated={deactivated}
          textAlignment={textAlignment}
          outlineVisibility="hover"
          onChange={handleNumberChange}
          onUnitClick={onUnitClick}
          onParseErrorChange={setInnerParseError}
          label={label}
          startAdornment={startAdornment}
          helperText={helperText}
          disabled={disabled}
          variant={variant}
          size={size}
          active={hasForcedActive ? true : undefined}
        />
      </Box>
    </Tooltip>
  );
};
